<script setup>
import {
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
  useTemplateRef,
} from 'vue';
import CarEquipmentDetailsTable from '@/components/CarEquipmentDetailsTable.vue';
import CheckList from '@/components/ui/CheckList.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});

const ICONS = ['available', 'disable', 'standard', 'option'];
const activeTab = ref(props?.data?.categories?.[0]?.id || null);

const tabListElement = useTemplateRef('tabList');

const scrollToActiveTab = () => {
  nextTick(() => {
    if (!tabListElement.value) return;
    const activeTabEl = tabListElement.value.$el.querySelector(
      '[data-p-active="true"], .p-tab-active, .p-active',
    );

    if (activeTabEl) {
      activeTabEl.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  });
};

watch(activeTab, () => {
  scrollToActiveTab();
});

const onTabListResize = () => {
  tabListElement.value?.updateInkBar();
};

onMounted(() => {
  window.addEventListener('resize', onTabListResize, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onTabListResize);
});
</script>
<template>
  <section
    :id="data?.id"
    class="section"
    :class="[
      data?.background_color ? `bg-${data?.background_color}` : '',
      data?.spacing?.top ? `section--top-${data?.spacing.top}` : '',
      data?.spacing?.bottom ? `section--bottom-${data?.spacing.bottom}` : '',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div class="car-equipment-details">
        <h2
          v-if="data?.title"
          class="mb-0 car-equipment-details__title h2 text-center"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          v-html="data?.title"
        ></h2>
        <div class="car-equipment-details__bottom">
          <template v-if="data?.categories?.length > 1">
            <Tabs
              v-model:value="activeTab"
              :scrollable="true"
              class="car-equipment-details__tabs"
              data-aos="fade-up"
              data-aos-easing="ease-out-cubic"
            >
              <TabList
                ref="tabList"
                :class="`car-equipment-details__tabs-headers tabs-bg-${data?.background_color}`"
              >
                <Tab
                  v-for="cat in data?.categories"
                  :key="cat?.id"
                  :value="cat?.id"
                  class="car-equipment-details__tabs-title"
                >
                  {{ cat?.title }}
                </Tab>
              </TabList>
              <TabPanels class="car-equipment-details__tabs-content">
                <TabPanel
                  v-for="cat in data?.categories"
                  :key="cat?.id"
                  :value="cat?.id"
                  class="car-equipment-details__tab"
                >
                  <CarEquipmentDetailsTable
                    v-if="cat?.type === 'table'"
                    :category-data="cat"
                    :vehicles="cat?.vehicles"
                    :initial-visible-rows="5"
                    :title="data?.title"
                  />
                  <div
                    v-if="cat?.type === 'list'"
                    class="car-equipment-details__list"
                  >
                    <CheckList
                      :data="cat?.groups"
                      :columns="cat?.columns_count"
                    />
                  </div>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </template>
          <template v-else>
            <div
              v-if="data?.categories[0]?.type === 'list'"
              class="car-equipment-details__list"
            >
              <CheckList
                :data="data?.categories[0]?.groups"
                :columns="data?.categories[0]?.columns_count"
              />
            </div>
          </template>
          <div
            v-if="data?.legend && data?.legend.length > 0"
            class="car-equipment-details__legend"
          >
            <template v-for="(item, index) in data.legend" :key="item.label">
              <div
                :class="[
                  ICONS.includes(item.value)
                    ? 'car-equipment-details__legend-item car-equipment-details__legend-item--icon'
                    : 'car-equipment-details__legend-item',
                ]"
                data-aos="fade-up"
                :data-aos-delay="index >= 1 ? 100 + 100 * index : 100"
              >
                <CIcon
                  v-if="ICONS.includes(item.value)"
                  :name="item.value"
                  width="32"
                  height="32"
                  role="img"
                  class="car-equipment-details__legend-item-icon"
                />
                <span v-else class="car-equipment-details__legend-item-value">{{
                  item.value
                }}</span>
                <span class="car-equipment-details__legend-item-label">{{
                  item.label
                }}</span>
              </div>
            </template>
          </div>
          <div
            v-if="data?.add_info && data?.add_info.length > 0"
            class="car-equipment-details__info mb-0 label"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="data?.add_info"
          ></div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use 'sass:math';
.car-equipment-details {
  display: flex;
  flex-direction: column;
  gap: 40px;
  @include media-breakpoint-up(sm) {
    gap: 80px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &__title {
    margin: 0;
  }
  &__tabs {
    display: flex;
    flex-direction: column;
    gap: 32px;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
    &-headers {
      &:deep(.p-tablist-active-bar) {
        height: 4px;
      }
      &:deep(.p-tablist-tab-list) {
        width: fit-content;
        margin: 0 auto;
      }
      @include media-breakpoint-up(sm) {
        display: flex;
        //justify-content: center;
        position: relative;

        &:deep(.p-tablist-tab-list) {
          // justify-content: center;
        }
      }
      @include media-breakpoint-up(xl) {
        &:deep(.p-tablist-active-bar) {
          @include responsive-spacing(height, 4);
        }
      }
    }
    &-title {
      @include link-normal;
      border-bottom: 1px solid $divider;
      color: $on-surface-variant-2;
      cursor: pointer;
      position: relative;
      padding: 16px;
      text-align: center;
      transition: color 0.2s ease;
      white-space: unset;
      width: auto;
      @include media-breakpoint-up(sm) {
        padding: 17px 16px 15px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-bottom-width, 1);
        @include responsive-spacing(padding-top, 17);
        @include responsive-spacing(padding-right, 16);
        @include responsive-spacing(padding-left, 16);
        @include responsive-spacing(padding-bottom, 15);
      }
      &[data-p='active'] {
        color: $on-surface;
      }
    }
    &-content {
      @include media-breakpoint-up(lg) {
      }
    }
  }
  &__list {
    background-color: $gray;
    border-radius: 8px;
    padding: 24px 24px 8px;
    @include media-breakpoint-up(sm) {
      padding: 40px 40px 16px;
    }
    @include media-breakpoint-up(md) {
      padding: 64px 64px 40px 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding-top, 64);
      @include responsive-spacing(padding-right, 64);
      @include responsive-spacing(padding-left, 48);
      @include responsive-spacing(padding-bottom, 40);
    }
  }
  &__bottom {
    display: flex;
    flex-direction: column;
    gap: 16px;
    @include media-breakpoint-up(sm) {
      gap: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 24);
    }
  }
  &__info {
    color: $on-surface-variant-2;
  }

  &__legend {
    display: flex;
    flex-direction: row;
    gap: 16px;
    flex-wrap: wrap;
    @include media-breakpoint-up(md) {
      gap: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 24);
    }
    &-item {
      align-items: center;
      display: grid;
      grid-template-columns: min-content auto;
      gap: 8px;
      @include media-breakpoint-up(md) {
        gap: 12px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 12);
      }
      &-value {
        font-weight: 700;
        text-align: center;
      }
    }
  }
}
</style>
